/**
 * 상태 → DOM 렌더. docs/DESIGN.md §3.1 표의 "시청자 뷰 / 스트리머 프리뷰" 열을 구현.
 *
 * 이 모듈은 "한 상태 = 한 DOM 조각". 메인 오케스트레이터(main.ts)가 transition을
 * 관리합니다. 여기서는 순수하게 상태 → Element를 생성.
 */
import type { Theme } from '@loop/shared/theme';
import type { ViewerMode } from '../viewer-mode.js';
import { sanitizeMessage, segmentMessage, truncateNickname } from '../nickname.js';
import type { WidgetState } from './types.js';

const PREVIEW_ONLY_KINDS = new Set<WidgetState['kind']>([
  'connecting',
  'reconnecting',
  'disconnected-fatal',
  'filter-triggered',
  'paused',
]);

/**
 * 시청자 뷰에서는 preview-only 상태들을 숨긴다 (투명 반환).
 * main.ts의 상태 전이가 이미 필터링하지만, 방어적으로 여기도 검증.
 */
export function shouldRenderForViewer(kind: WidgetState['kind'], mode: ViewerMode): boolean {
  if (mode === 'preview') return true;
  // 예외: `paused` 는 시청자에게 완전 투명이어야 함 → false.
  if (PREVIEW_ONLY_KINDS.has(kind)) return false;
  return true;
}

export function formatKrw(amount: number): string {
  return `${amount.toLocaleString('ko-KR')}원`;
}

function applyTheme(el: HTMLElement, theme: Theme): void {
  el.style.color = theme.color.text;
  el.style.fontFamily = theme.typography.fontFamily.text;
  el.style.fontSize = `${theme.typography.fontSize.base}px`;
  el.style.letterSpacing = `${theme.typography.letterSpacing}px`;
  if (theme.shape.elevation) el.style.boxShadow = theme.shape.elevation;
  el.style.borderRadius = `${theme.shape.borderRadius}px`;
  if (theme.shape.borderWidth > 0) {
    el.style.border = `${theme.shape.borderWidth}px solid ${theme.color.stroke}`;
  }
  if (theme.color.glow) {
    el.style.textShadow = `0 0 8px ${theme.color.glow}`;
  }
}

function renderDonationCard(
  state: Extract<WidgetState, { kind: 'donation-incoming' }>,
  theme: Theme,
  mode: ViewerMode,
): HTMLElement {
  const card = document.createElement('div');
  card.className = `loop-card loop-card--${state.tier}`;
  card.setAttribute('role', 'status');
  applyTheme(card, theme);
  card.style.padding = `${theme.shape.padding.toast}px`;
  card.style.color = theme.color.tiers[state.tier];
  card.style.display = 'flex';
  card.style.flexDirection = 'column';
  card.style.gap = `${theme.shape.gap}px`;

  const meta = document.createElement('div');
  meta.className = 'loop-card__meta';
  meta.style.display = 'flex';
  meta.style.alignItems = 'baseline';
  meta.style.gap = `${theme.shape.gap}px`;

  const nick = document.createElement('span');
  nick.className = 'loop-card__nick';
  nick.textContent = truncateNickname(state.event.nickname);

  const amount = document.createElement('span');
  amount.className = 'loop-card__amount';
  amount.style.fontFamily = theme.typography.fontFamily.number;
  amount.style.fontWeight = String(theme.typography.fontWeight.bold);
  amount.style.fontSize = `${theme.typography.fontSize.lg}px`;
  amount.textContent = formatKrw(state.event.amount);

  meta.append(nick, amount);
  card.append(meta);

  const msg = sanitizeMessage(state.event.message);
  if (msg.length > 0) {
    const line = document.createElement('div');
    line.className = 'loop-card__msg';
    line.style.fontSize = `${theme.typography.fontSize.sm}px`;
    for (const seg of segmentMessage(msg)) {
      const s = document.createElement('span');
      s.textContent = seg.value;
      if (seg.kind === 'url') s.style.color = theme.color.accent;
      line.appendChild(s);
    }
    card.append(line);
  }

  // 스트리머 프리뷰에서만 삭제 버튼 노출 (§3.1 #7 large의 경우 특히 필요).
  if (mode === 'preview') {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'loop-card__del';
    btn.dataset.action = 'delete-donation';
    btn.dataset.donationId = state.event.id;
    btn.textContent = '삭제';
    btn.style.fontSize = `${theme.typography.fontSize.sm}px`;
    btn.style.marginTop = `${theme.shape.gap}px`;
    btn.style.opacity = '0.6';
    card.append(btn);
  }
  return card;
}

export function renderState(state: WidgetState, theme: Theme, mode: ViewerMode): HTMLElement {
  const host = document.createElement('div');
  host.dataset.state = state.kind;
  host.dataset.mode = mode;
  host.style.pointerEvents = 'none';

  if (!shouldRenderForViewer(state.kind, mode)) return host; // 투명.

  switch (state.kind) {
    case 'idle': {
      if (mode === 'preview') {
        const dot = document.createElement('span');
        dot.className = 'loop-live-dot';
        dot.style.position = 'fixed';
        dot.style.left = '1vw';
        dot.style.bottom = '1vw';
        dot.style.fontSize = '12px';
        dot.style.opacity = '0.5';
        dot.style.color = theme.color.accent;
        dot.textContent = '● LIVE';
        host.append(dot);
      }
      return host;
    }
    case 'connecting': {
      const el = document.createElement('div');
      el.textContent = '● connecting…';
      el.style.position = 'fixed';
      el.style.left = '1vw';
      el.style.bottom = '1vw';
      el.style.color = theme.color.accent;
      el.style.animation = 'loop-blink 1s infinite';
      host.append(el);
      return host;
    }
    case 'reconnecting': {
      const el = document.createElement('div');
      el.textContent = `⚠ 재접속 중 (${state.attemptIn}s)`;
      el.style.position = 'fixed';
      el.style.left = '1vw';
      el.style.bottom = '1vw';
      el.style.color = '#F2C07A';
      host.append(el);
      return host;
    }
    case 'disconnected-fatal': {
      const el = document.createElement('div');
      el.textContent = `✕ 연결 실패: ${state.reason} · 대시보드에서 재연결하세요`;
      el.setAttribute('role', 'alert');
      el.style.position = 'fixed';
      el.style.left = '1vw';
      el.style.top = '1vw';
      el.style.color = '#F27A7A';
      host.append(el);
      return host;
    }
    case 'donation-incoming': {
      const card = renderDonationCard(state, theme, mode);
      card.style.position = 'fixed';
      if (state.tier === 'large') {
        card.style.left = '50%';
        card.style.top = '50%';
        card.style.transform = 'translate(-50%, -50%)';
      } else if (state.tier === 'medium') {
        card.style.left = '50%';
        card.style.top = '80%';
        card.style.transform = 'translate(-50%, -50%)';
      } else {
        card.style.right = '2vw';
        card.style.bottom = '2vw';
      }
      host.append(card);
      return host;
    }
    case 'tts-playing': {
      const el = document.createElement('div');
      el.className = 'loop-tts';
      el.setAttribute('aria-live', 'polite');
      el.style.position = 'fixed';
      el.style.left = '50%';
      el.style.bottom = '10vh';
      el.style.transform = 'translateX(-50%)';
      el.style.color = theme.color.text;
      el.style.fontFamily = theme.typography.fontFamily.text;
      el.style.maxWidth = '70vw';
      el.style.padding = `${theme.shape.padding.toast}px`;
      el.textContent = state.text;
      if (mode === 'preview') {
        const skip = document.createElement('button');
        skip.type = 'button';
        skip.dataset.action = 'skip-tts';
        skip.textContent = 'skip';
        skip.style.marginLeft = '8px';
        skip.style.opacity = '0.6';
        el.append(skip);
      }
      host.append(el);
      return host;
    }
    case 'filter-triggered': {
      // 시청자 뷰는 이미 shouldRenderForViewer에서 걸러짐. preview만 여기 도달.
      const el = document.createElement('div');
      el.setAttribute('role', 'status');
      el.style.position = 'fixed';
      el.style.left = '50%';
      el.style.top = '2vh';
      el.style.transform = 'translateX(-50%)';
      el.style.color = '#F27A7A';
      el.style.background = 'rgba(0,0,0,0.6)';
      el.style.padding = '8px 12px';
      el.style.borderRadius = `${theme.shape.borderRadius}px`;
      el.textContent = `차단됨: ${state.blockedReason} · 원본: "${state.originalText.slice(0, 40)}"`;
      host.append(el);
      return host;
    }
    case 'burst-queue': {
      const badge = document.createElement('div');
      badge.style.position = 'fixed';
      badge.style.left = '2vw';
      badge.style.top = '2vh';
      badge.style.padding = '4px 10px';
      badge.style.borderRadius = '999px';
      badge.style.background = 'rgba(0,0,0,0.5)';
      badge.style.color = theme.color.accent;
      badge.style.fontFamily = theme.typography.fontFamily.number;
      if (state.currentAggregateAmount !== undefined) {
        badge.textContent = `${state.remaining}명 · ${formatKrw(state.currentAggregateAmount)}`;
      } else {
        badge.textContent = `+${state.remaining}`;
      }
      host.append(badge);
      return host;
    }
    case 'goal-reached': {
      const card = document.createElement('div');
      card.setAttribute('role', 'status');
      card.style.position = 'fixed';
      card.style.left = '50%';
      card.style.top = '40%';
      card.style.transform = 'translate(-50%, -50%)';
      card.style.textAlign = 'center';
      card.style.fontFamily = theme.typography.fontFamily.text;
      card.style.color = theme.color.tiers.large;
      if (theme.color.glow) card.style.textShadow = `0 0 24px ${theme.color.glow}`;

      const headline = document.createElement('div');
      headline.style.fontSize = `${theme.typography.fontSize.lg * 1.4}px`;
      headline.style.fontWeight = String(theme.typography.fontWeight.bold);
      headline.textContent = '🎯 목표 달성!';

      const sub = document.createElement('div');
      sub.style.fontSize = `${theme.typography.fontSize.base}px`;
      sub.style.marginTop = '8px';
      sub.textContent = `${state.goalLabel} — ${formatKrw(state.goalAmount)}`;

      card.append(headline, sub);
      host.append(card);
      return host;
    }
    case 'paused': {
      // shouldRenderForViewer가 이미 false를 리턴. preview만 도달.
      const veil = document.createElement('div');
      veil.style.position = 'fixed';
      veil.style.inset = '0';
      veil.style.background = 'rgba(0,0,0,0.3)';
      veil.style.display = 'grid';
      veil.style.placeItems = 'center';
      veil.style.color = '#F2C07A';
      veil.textContent = '일시정지됨 (스트리머 뷰 전용)';
      host.append(veil);
      return host;
    }
  }
}

/**
 * 위젯에 필요한 글로벌 CSS (keyframes, blink 등)를 head에 한 번만 주입.
 */
let cssInstalled = false;
export function installWidgetCss(): void {
  if (cssInstalled || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = `
@keyframes loop-blink { 0%,100% { opacity: 0.3; } 50% { opacity: 1; } }
[data-state] { position: absolute; inset: 0; }
button[data-action] { cursor: pointer; background: rgba(0,0,0,0.5); color: inherit; border: 1px solid currentColor; padding: 2px 8px; border-radius: 4px; pointer-events: auto; }
  `;
  document.head.appendChild(style);
  cssInstalled = true;
}
